import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { foldForSearch } from '../domain/text.ts';
import { Dialog } from './Dialog.tsx';
import { CheckIcon, ChevronDownIcon, SearchIcon } from './icons.tsx';
import { uppercaseChange } from './uppercase.ts';
import { useMediaQuery } from './use-media-query.ts';

export interface ListOption {
  value: string;
  label: string;
  /** Clase de estado (p. ej. `status-listo`) para mostrar su punto de color. */
  tone?: string;
  /** Clase para el texto de la opción (p. ej. `unassigned`), en la lista y en el campo al elegirla. */
  labelClassName?: string;
}

interface ListSelectProps {
  label: string;
  value: string;
  options: ListOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  hint?: string | undefined;
  required?: boolean;
  error?: string | undefined;
  /** Texto mientras el valor no coincide con ninguna opción. Sin él se muestra la primera opción. */
  placeholder?: string;
  /** Muestra un buscador sobre las opciones; su texto es la etiqueta accesible del buscador. */
  searchLabel?: string;
}

// Lista desplegable a medida (listbox), en lugar del <select> nativo: se abre al tocar el campo y
// se cierra al elegir, con Escape o al tocar fuera. Teclado: flechas, Inicio y Fin para moverse;
// Enter o Espacio para elegir. Con buscador, el foco pasa al buscador y las flechas siguen moviendo
// la opción activa. Sigue el patrón de DateField (cierre al tocar fuera).
const MOBILE_QUERY = '(max-width: 599px)';

const optionLabelClass = (option: ListOption) =>
  option.labelClassName ? `list-select-option-label ${option.labelClassName}` : 'list-select-option-label';

export function ListSelect({
  label,
  value,
  options,
  onChange,
  disabled = false,
  hint,
  required = false,
  error,
  placeholder,
  searchLabel,
}: ListSelectProps) {
  // En móvil las opciones se eligen en un panel inferior (bottom sheet); el resto usa el desplegable.
  const mobile = useMediaQuery(MOBILE_QUERY);
  const labelId = useId();
  const listId = useId();
  const hintId = hint ? `${labelId}-hint` : undefined;
  const errorId = error ? `${labelId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const sheetList = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const matchIndex = options.findIndex((option) => option.value === value);
  const selected = matchIndex >= 0 ? options[matchIndex] : placeholder === undefined ? options[0] : undefined;
  const folded = foldForSearch(query);
  const visible = folded ? options.filter((option) => foldForSearch(option.label).includes(folded)) : options;
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!open || mobile) return;
    const onPointer = (event: PointerEvent) => {
      if (wrapper.current && !wrapper.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, mobile]);

  useEffect(() => {
    if (open && !mobile) document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, mobile, active, listId]);

  // En escritorio se escribe en el buscador nada más abrir. En móvil el foco va a la lista para que el
  // teclado no tape las opciones; se abre al tocar el buscador. (El panel ya abrió: su efecto corre antes.)
  useEffect(() => {
    if (!open || !searchLabel) return;
    if (mobile) sheetList.current?.focus();
    else search.current?.focus();
  }, [open, mobile, searchLabel]);

  function openList() {
    if (disabled) return;
    setQuery('');
    setActive(Math.max(0, matchIndex));
    setOpen(true);
  }

  function close() {
    setOpen(false);
    if (!mobile) trigger.current?.focus();
  }

  function choose(option: ListOption | undefined) {
    if (option) onChange(option.value);
    close();
  }

  function onSearch(text: string) {
    setQuery(text);
    setActive(0);
  }

  // Teclas comunes al campo y al buscador; el campo además usa Espacio, Inicio y Fin.
  function navigate(event: KeyboardEvent<HTMLElement>): boolean {
    switch (event.key) {
      case 'ArrowDown':
        setActive((i) => Math.min(i + 1, visible.length - 1));
        break;
      case 'ArrowUp':
        setActive((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        choose(visible[active]);
        break;
      case 'Escape':
        close();
        break;
      case 'Tab':
        setOpen(false);
        return true;
      default:
        return false;
    }
    event.preventDefault();
    return true;
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (navigate(event)) return;
    switch (event.key) {
      case 'Home':
        event.preventDefault();
        setActive(0);
        break;
      case 'End':
        event.preventDefault();
        setActive(visible.length - 1);
        break;
      case ' ':
        event.preventDefault();
        choose(visible[active]);
        break;
    }
  }

  const searchField = searchLabel ? (
    <div className="list-select-search">
      <SearchIcon />
      <input
        ref={search}
        type="search"
        role="combobox"
        aria-label={searchLabel}
        aria-expanded="true"
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={!mobile && visible[active] ? `${listId}-${active}` : undefined}
        placeholder="Buscar…"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        value={query}
        onChange={uppercaseChange((event) => onSearch(event.target.value))}
        onKeyDown={(event) => void navigate(event)}
      />
    </div>
  ) : null;
  const empty = visible.length === 0 ? <p className="list-select-empty">Sin coincidencias.</p> : null;

  return (
    <div
      className={`field list-select${open ? ' list-select-open' : ''}${error ? ' field-invalid' : ''}`}
      ref={wrapper}
    >
      <span id={labelId} className="list-select-label">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </span>
      <div
        ref={trigger}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-labelledby={labelId}
        aria-describedby={describedBy}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && !searchLabel ? `${listId}-${active}` : undefined}
        className="list-select-trigger"
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
      >
        <span className="list-select-value">
          {selected ? (
            <>
              {selected.tone ? <span className={`list-select-dot ${selected.tone}`} aria-hidden="true" /> : null}
              {selected.labelClassName ? (
                <span className={selected.labelClassName}>{selected.label}</span>
              ) : (
                selected.label
              )}
            </>
          ) : (
            <span className="list-select-placeholder">{placeholder}</span>
          )}
        </span>
        <span className="list-select-chevron" aria-hidden="true">
          <ChevronDownIcon />
        </span>
      </div>
      {hint ? (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      {open && !mobile ? (
        <div className={`list-select-panel${searchLabel ? ' list-select-panel-search' : ''}`}>
          {searchField}
          <ul id={listId} role="listbox" aria-labelledby={labelId} className="list-select-options">
            {visible.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                className={`list-select-option${index === active ? ' list-select-option-active' : ''}`}
                onPointerEnter={() => setActive(index)}
                onClick={() => choose(option)}
              >
                {option.tone ? <span className={`list-select-dot ${option.tone}`} aria-hidden="true" /> : null}
                <span className={optionLabelClass(option)}>{option.label}</span>
                {option.value === value ? <CheckIcon /> : null}
              </li>
            ))}
          </ul>
          {empty}
        </div>
      ) : null}
      {mobile ? (
        <Dialog
          open={open}
          title={label}
          onClose={() => setOpen(false)}
          footer={
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>
              Cerrar
            </button>
          }
        >
          {searchField}
          <ul
            ref={sheetList}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={labelId}
            className="list-select-sheet"
          >
            {visible.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                className="list-select-option"
                onClick={() => choose(option)}
              >
                {option.tone ? <span className={`list-select-dot ${option.tone}`} aria-hidden="true" /> : null}
                <span className={optionLabelClass(option)}>{option.label}</span>
                {option.value === value ? <CheckIcon /> : null}
              </li>
            ))}
          </ul>
          {empty}
        </Dialog>
      ) : null}
    </div>
  );
}
